import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Chip,
  Fade,
  Dialog,
} from '@mui/material';
import {
  Close,
  Edit,
  Cancel,
  Receipt,
  AccessTime,
  ViewList,
  ViewModule,
  CheckCircle,
  Print,
  Refresh,
  ContentCopy,
  TimerOff,
  ErrorOutline,
  CheckCircleOutline,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useOrdersStore } from '../../stores/ordersStore.js';
import { useAuth } from '../../hooks/useAuth.js';
import { OrderService } from '../../services/orderService.js';
import { ReceiptPrinterService } from '../../services/receiptPrinterService.js';
import { BillingService } from '../../services/billingService.js';
import { QRbarService } from '../../services/qrbarService.js';

/**
 * Mobile Orders tab — shows ONLY active orders.
 * Separate from cart (current order).
 */
export const MobileOrdersTab = () => {
  const { outletId, outletName } = useAuth();
  const { activeOrders, fetchActiveOrders, isLoading: ordersLoading } = useOrdersStore();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeOrdersLayout, setActiveOrdersLayout] = useState('list');
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [printStatus, setPrintStatus] = useState(null);
  const [closeCountdown, setCloseCountdown] = useState(null);
  const closeTimerRef = useRef(null);
  const countdownRef = useRef(null);

  // ── Billing / QR inline state ──────────────────────────────────
  const [billingActive, setBillingActive] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [billingError, setBillingError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [qrbarConnected, setQrbarConnected] = useState(false);
  const [qrFullscreen, setQrFullscreen] = useState(false);
  const pollRef = useRef(null);
  const billingTimerRef = useRef(null);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const handleCancelOrder = async (order) => {
    setActionLoading('cancel');
    try {
      await OrderService.updateOrderStatus(order.OrderID, 'Cancelled');
      setSelectedOrder(null);
      setOrderCompleted(false);
      if (outletId) fetchActiveOrders(outletId);
    } catch (error) {
      console.error('Error cancelling order:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmPayment = async (order) => {
    setActionLoading('confirm');
    try {
      await OrderService.updateOrderStatus(order.OrderID, 'Completed');
      setOrderCompleted(true);
    } catch (error) {
      console.error('Error completing order:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const startCloseTimer = () => {
    // Clear any existing timer
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    setCloseCountdown(10);
    countdownRef.current = setInterval(() => {
      setCloseCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    closeTimerRef.current = setTimeout(() => {
      handleDeselectOrder();
    }, 10000);
  };

  const handlePrintReceipt = async () => {
    setPrintStatus('printing');
    try {
      await ReceiptPrinterService.printReceipt(selectedOrder, outletName);
      setPrintStatus('done');
      startCloseTimer();
    } catch (err) {
      console.error('[MobileOrdersTab] Print failed:', err);
      setPrintStatus('error');
      setTimeout(() => setPrintStatus(null), 3000);
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleDeselectOrder = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    cleanupBilling();
    setSelectedOrder(null);
    setOrderCompleted(false);
    setPrintStatus(null);
    setCloseCountdown(null);
    if (outletId) fetchActiveOrders(outletId);
  };

  // ── Generate Bill (inline) ──────────────────────────────────────
  const handleGenerateBill = useCallback(async (order) => {
    if (!order?.OrderID || !outletId) return;

    setBillingActive(true);
    setSessionStatus('creating');
    setBillingError(null);
    setSessionData(null);
    setCopied(false);

    try {
      const health = await QRbarService.checkHealth();
      setQrbarConnected(health.connected);

      const session = await BillingService.createPaymentSession(order.OrderID, outletId);
      setSessionData(session);
      setSessionStatus('active');

      if (session.expires_at) {
        const secondsLeft = Math.max(0, Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 1000));
        setExpiresIn(secondsLeft > 0 ? secondsLeft : 900);
      } else {
        setExpiresIn(900);
      }

      if (health.connected && session.qr_url) {
        await QRbarService.displayQR(session.qr_url);
      }
    } catch (err) {
      setBillingError(err.message || 'Failed to create payment session');
      setSessionStatus('error');
    }
  }, [outletId]);

  const cleanupBilling = useCallback(async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (billingTimerRef.current) clearInterval(billingTimerRef.current);

    if (sessionData?.session_token && (sessionStatus === 'active' || sessionStatus === 'fetched' || sessionStatus === 'payment_initiated')) {
      try { await BillingService.expireSession(sessionData.session_token); } catch {}
    }
    if (qrbarConnected) { try { await QRbarService.clearDisplay(); } catch {} }

    setBillingActive(false);
    setSessionData(null);
    setSessionStatus(null);
    setExpiresIn(0);
    setBillingError(null);
    setCopied(false);
    setQrFullscreen(false);
  }, [sessionData?.session_token, sessionStatus, qrbarConnected]);

  // ── Billing countdown timer ────────────────────────────────────
  useEffect(() => {
    if (!billingActive) return;
    if (sessionStatus !== 'active' && sessionStatus !== 'fetched' && sessionStatus !== 'payment_initiated') {
      if (billingTimerRef.current) clearInterval(billingTimerRef.current);
      return;
    }
    billingTimerRef.current = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev <= 1) { clearInterval(billingTimerRef.current); setSessionStatus('expired'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (billingTimerRef.current) clearInterval(billingTimerRef.current); };
  }, [billingActive, sessionStatus]);

  // ── Poll session status ────────────────────────────────────────
  useEffect(() => {
    if (!billingActive) return;
    if (sessionStatus !== 'active' && sessionStatus !== 'fetched' && sessionStatus !== 'payment_initiated') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const sessions = await BillingService.getSessionStatuses(outletId);
        const mySession = sessions.find((s) => s.session_token === sessionData?.session_token);
        if (!mySession) return;
        const st = mySession.status?.toLowerCase();
        if (st === 'paid') {
          setSessionStatus('paid');
          clearInterval(pollRef.current);
          if (selectedOrder?.OrderID) { try { await OrderService.updateOrderStatus(selectedOrder.OrderID, 'Completed'); } catch {} }
          if (qrbarConnected) { try { await QRbarService.clearDisplay(); } catch {} }
          setOrderCompleted(true);
        } else if (st === 'expired') {
          setSessionStatus('expired');
          clearInterval(pollRef.current);
        } else if (st === 'payment_initiated') {
          setSessionStatus('payment_initiated');
        } else if (st === 'fetched') {
          setSessionStatus('fetched');
        }
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [billingActive, sessionStatus, sessionData?.session_token, outletId, selectedOrder?.OrderID, qrbarConnected]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (billingTimerRef.current) clearInterval(billingTimerRef.current);
    };
  }, []);

  const formatCountdown = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleCopyUrl = () => {
    if (sessionData?.qr_url) {
      navigator.clipboard.writeText(sessionData.qr_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleManualPaymentReceived = async () => {
    setActionLoading('confirm');
    try {
      await OrderService.updateOrderStatus(selectedOrder.OrderID, 'Completed');
      setSessionStatus('paid');
      setOrderCompleted(true);
      if (qrbarConnected) { try { await QRbarService.clearDisplay(); } catch {} }
    } catch { setBillingError('Failed to confirm payment'); }
    finally { setActionLoading(null); }
  };

  const getStatusDisplay = () => {
    switch (sessionStatus) {
      case 'active': return { label: 'Waiting for customer to scan', color: '#FF9500', pulse: true };
      case 'fetched': return { label: 'Customer is viewing the bill', color: '#007AFF', pulse: true };
      case 'payment_initiated': return { label: 'Payment in progress...', color: '#34C759', pulse: true };
      case 'paid': return { label: 'Payment received!', color: '#34C759', pulse: false };
      case 'expired': return { label: 'Session expired', color: '#FF3B30', pulse: false };
      default: return { label: '', color: 'text.secondary', pulse: false };
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          borderBottom: '1px solid #C8C8CC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Active Orders
          {activeOrders.length > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 0.9,
                py: 0.15,
                borderRadius: '7px',
                bgcolor: 'secondary.main',
                color: 'rgba(255, 255, 255, 0.72)',
                fontSize: '0.65rem',
                fontWeight: 800,
                verticalAlign: 'middle',
              }}
            >
              {activeOrders.length}
            </Box>
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="List view">
            <IconButton
              size="small"
              onClick={() => setActiveOrdersLayout('list')}
              sx={{
                width: 32,
                height: 32,
                color: activeOrdersLayout === 'list' ? '#4527A0' : 'rgba(0, 0, 0, 0.15)',
                bgcolor: activeOrdersLayout === 'list' ? 'rgba(69, 39, 160, 0.08)' : 'transparent',
              }}
            >
              <ViewList sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Grid view">
            <IconButton
              size="small"
              onClick={() => setActiveOrdersLayout('grid')}
              sx={{
                width: 32,
                height: 32,
                color: activeOrdersLayout === 'grid' ? '#4527A0' : 'rgba(0, 0, 0, 0.15)',
                bgcolor: activeOrdersLayout === 'grid' ? 'rgba(69, 39, 160, 0.08)' : 'transparent',
              }}
            >
              <ViewModule sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => outletId && fetchActiveOrders(outletId)}
            disabled={ordersLoading}
            sx={{ color: 'text.disabled' }}
          >
            {ordersLoading ? (
              <CircularProgress size={16} sx={{ color: 'text.disabled' }} />
            ) : (
              <AccessTime sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {selectedOrder ? (
          /* Order Detail View */
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%' }}>
            {/* Sticky Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                Order #{selectedOrder.OrderNum}
              </Typography>
              <IconButton
                size="small"
                onClick={handleDeselectOrder}
                sx={{ bgcolor: 'background.default', width: 30, height: 30 }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Scrollable Items list */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '14px',
                border: '1px solid #C8C8CC',
                bgcolor: 'rgba(255, 255, 255, 0.72)',
              }}
            >
              {(selectedOrder.ItemNames || []).map((name, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1.5,
                    py: 1,
                    borderBottom:
                      idx < selectedOrder.ItemNames.length - 1 ? '1px solid #E0E0E3' : 'none',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                    {name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.85rem' }}>
                    ×{selectedOrder.Quantity?.[idx] || 1}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Sticky Footer: Total + Action Buttons / Billing */}
            <Box sx={{ flexShrink: 0, pt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Total</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  ₹{(selectedOrder.Amount || 0).toFixed(2)}
                </Typography>
              </Box>

              {orderCompleted ? (
                /* ── Order Completed State ── */
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#34C759', mb: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#34C759' }}>
                      Payment Confirmed
                    </Typography>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Print sx={{ fontSize: 16 }} />}
                    onClick={handlePrintReceipt}
                    disabled={printStatus === 'printing'}
                    sx={{
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      py: 1,
                      bgcolor: printStatus === 'done' ? '#1D1D1F' : printStatus === 'error' ? '#FF3B30' : '#1D1D1F',
                      '&:hover': { bgcolor: printStatus === 'done' ? '#1D1D1F' : printStatus === 'error' ? '#D32F2F' : '#1D1D1F' },
                    }}
                  >
                    {printStatus === 'printing' ? 'Printing...' : printStatus === 'done' ? 'Print Again' : printStatus === 'error' ? 'Print Failed — Retry' : 'Print Receipt'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleDeselectOrder}
                    sx={{
                      mt: 0.75,
                      borderRadius: '10px',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      py: 0.85,
                      color: '#FF3B30',
                      borderColor: 'rgba(255, 59, 48, 0.12)',
                      '&:hover': { borderColor: '#FF3B30', bgcolor: 'rgba(255,59,48,0.04)' },
                    }}
                  >
                    {closeCountdown != null && closeCountdown > 0 ? `Close (${closeCountdown}s)` : 'Close'}
                  </Button>
                </Box>
              ) : billingActive ? (
                /* ── Inline Billing / QR Section ── */
                <Box>
                  {sessionStatus === 'creating' && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <CircularProgress size={28} sx={{ color: 'primary.main', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.82rem' }}>
                        Generating payment QR...
                      </Typography>
                    </Box>
                  )}

                  {sessionStatus === 'error' && (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <ErrorOutline sx={{ fontSize: 32, color: '#FF3B30', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', mb: 0.5 }}>
                        Failed to generate QR
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                        {billingError}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh sx={{ fontSize: 14 }} />}
                        onClick={() => handleGenerateBill(selectedOrder)}
                        sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem', bordercolor: 'primary.main', color: 'primary.main' }}
                      >
                        Retry
                      </Button>
                    </Box>
                  )}

                  {sessionStatus === 'expired' && (
                    <Fade in timeout={400}>
                      <Box sx={{ py: 2, textAlign: 'center' }}>
                        <TimerOff sx={{ fontSize: 32, color: '#FF9500', mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', mb: 0.5 }}>
                          QR Code Expired
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Refresh sx={{ fontSize: 14 }} />}
                          onClick={() => handleGenerateBill(selectedOrder)}
                          sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                          Generate New QR
                        </Button>
                      </Box>
                    </Fade>
                  )}

                  {sessionStatus === 'paid' && (
                    <Fade in timeout={600}>
                      <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(52,199,89,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                          <CheckCircleOutline sx={{ fontSize: 30, color: '#34C759' }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5 }}>
                          Payment Received!
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          ₹{(selectedOrder.Amount || 0).toFixed(2)} — Order #{selectedOrder.OrderNum}
                        </Typography>
                      </Box>
                    </Fade>
                  )}

                  {(sessionStatus === 'active' || sessionStatus === 'fetched' || sessionStatus === 'payment_initiated') && sessionData?.qr_url && (
                    <Fade in timeout={400}>
                      <Box sx={{ textAlign: 'center' }}>
                        {/* QR Code — tappable */}
                        <Box
                          onClick={() => setQrFullscreen(true)}
                          sx={{
                            display: 'inline-flex',
                            p: 1.5,
                            bgcolor: 'rgba(255, 255, 255, 0.72)',
                            borderRadius: '14px',
                            border: '2px solid #F2F2F7',
                            mb: 1.5,
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease',
                            '&:hover': { transform: 'scale(1.03)' },
                            '&:active': { transform: 'scale(0.97)' },
                          }}
                        >
                          <QRCodeSVG
                            value={sessionData.qr_url}
                            size={160}
                            level="M"
                            bgColor="#FFFFFF"
                            fgColor="#1C1C1E"
                            style={{ display: 'block' }}
                          />
                        </Box>

                        <Typography variant="caption" sx={{ color: qrbarConnected ? '#34C759' : '#FF9500', fontSize: '0.68rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                          {qrbarConnected ? '● QRbar device connected' : '● No QRbar device connected'}
                        </Typography>

                        <Box sx={{ mb: 1 }}>
                          <Chip
                            label={getStatusDisplay().label}
                            size="small"
                            sx={{
                              bgcolor: `${getStatusDisplay().color}18`,
                              color: getStatusDisplay().color,
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              height: 24,
                              animation: getStatusDisplay().pulse ? 'pulse-opacity 2s ease-in-out infinite' : 'none',
                              '@keyframes pulse-opacity': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: 'monospace', color: expiresIn < 120 ? '#FF3B30' : '#1D1D1F' }}>
                            {formatCountdown(expiresIn)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                            remaining
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          startIcon={<ContentCopy sx={{ fontSize: 12 }} />}
                          onClick={handleCopyUrl}
                          sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.72rem', color: copied ? '#34C759' : '#86868B', textTransform: 'none', mb: 1 }}
                        >
                          {copied ? 'Copied!' : 'Copy link'}
                        </Button>

                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={actionLoading === 'confirm' ? <CircularProgress size={14} /> : <CheckCircleOutline sx={{ fontSize: 16 }} />}
                          onClick={handleManualPaymentReceived}
                          disabled={!!actionLoading}
                          sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', py: 0.75, color: '#34C759', borderColor: 'rgba(52, 199, 89, 0.15)', '&:hover': { borderColor: '#34C759', bgcolor: 'rgba(52,199,89,0.04)' } }}
                        >
                          Paid
                        </Button>
                      </Box>
                    </Fade>
                  )}
                </Box>
              ) : (
                /* ── Active Order Actions ── */
                <Box>
                  {/* Cancel + Confirm Payment row */}
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={actionLoading === 'cancel' ? <CircularProgress size={14} /> : <Cancel sx={{ fontSize: 16 }} />}
                      onClick={() => handleCancelOrder(selectedOrder)}
                      disabled={!!actionLoading}
                      sx={{
                        borderRadius: '10px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        py: 0.85,
                        color: '#FF3B30',
                        borderColor: 'rgba(255, 59, 48, 0.12)',
                        '&:hover': { borderColor: '#FF3B30', bgcolor: 'rgba(255,59,48,0.04)' },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={actionLoading === 'confirm' ? <CircularProgress size={14} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                      onClick={() => handleConfirmPayment(selectedOrder)}
                      disabled={!!actionLoading}
                      sx={{
                        borderRadius: '10px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        py: 0.85,
                        color: '#34C759',
                        borderColor: 'rgba(52, 199, 89, 0.15)',
                        '&:hover': { borderColor: '#34C759', bgcolor: 'rgba(52,199,89,0.04)' },
                      }}
                    >
                      Paid
                    </Button>
                  </Box>

                  {/* Generate Bill */}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Receipt sx={{ fontSize: 16 }} />}
                    onClick={() => handleGenerateBill(selectedOrder)}
                    disabled={!!actionLoading}
                    sx={{
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      py: 1,
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    Generate Bill
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        ) : activeOrders.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <AccessTime sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
              No active orders
            </Typography>
          </Box>
        ) : activeOrdersLayout === 'grid' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, alignContent: 'start' }}>
            {activeOrders.map((order, idx) => (
              <Box
                key={order.OrderID || `order-${idx}`}
                onClick={() => setSelectedOrder(order)}
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: 'rgba(255, 255, 255, 0.72)',
                  border: '1px solid #E0E0E3',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }} noWrap>
                  #{order.OrderNum}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.88rem', mt: 0.25 }}>
                  ₹{(order.Amount || 0).toFixed(0)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block', mt: 0.35 }}>
                  {formatTime(order.CreatedAt)}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activeOrders.map((order, idx) => (
              <Box
                key={order.OrderID || `order-${idx}`}
                onClick={() => setSelectedOrder(order)}
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: 'rgba(255, 255, 255, 0.72)',
                  border: '1px solid #E0E0E3',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:active': { transform: 'scale(0.98)' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                    #{order.OrderNum}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.88rem' }}>
                    ₹{(order.Amount || 0).toFixed(0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}
                  >
                    {(order.ItemNames || []).join(', ')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                    {formatTime(order.CreatedAt)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* QR Fullscreen Overlay */}
      <Dialog
        open={qrFullscreen}
        onClose={() => setQrFullscreen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '20px', p: 3, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.72)' },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setQrFullscreen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.default', width: 32, height: 32 }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
        {sessionData?.qr_url && (
          <Box sx={{ pt: 1 }}>
            <QRCodeSVG
              value={sessionData.qr_url}
              size={280}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#1C1C1E"
              style={{ display: 'block', margin: '0 auto' }}
            />
            <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
              Scan to pay — Order #{selectedOrder?.OrderNum}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};
